import React, { useCallback, useState, useRef } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { TranscriptTab } from "./TranscriptTab";
import { ProcessedTab } from "./ProcessedTab";
import type { ActiveTab } from "./types/recordHeader.types";
import { useTranslation } from "react-i18next";

interface RecordData {
  id: string;
  name: string;
  transcriptText: string;
  postProcessingText: string;
}

export interface RecordTabsSectionProps {
  activeTab: ActiveTab;
  onTabChange: (tab: ActiveTab) => void;
  record: RecordData;
  onProcessedTextUpdate?: (newText: string) => void;
  readOnly?: boolean;
  onSaveRequest?: () => Promise<void>;
  onEditingStateChange?: (isEditing: boolean) => void;
}

const TAB_TRIGGER_STYLES =
  "px-6 py-3 rounded-t-lg border-b-2 border-transparent data-[state=active]:border-brand-500 data-[state=active]:bg-card data-[state=active]:text-brand-600 data-[state=active]:shadow-sm hover:bg-muted/50 transition-all relative font-medium";

export const RecordTabsSection: React.FC<RecordTabsSectionProps> = React.memo(
  ({
    activeTab,
    onTabChange,
    record,
    onProcessedTextUpdate,
    readOnly = false,
    onSaveRequest,
    onEditingStateChange,
  }) => {
    const { t } = useTranslation();
    const [isEditing, setIsEditing] = useState(false);
    const [hasChanges, setHasChanges] = useState(false);
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);
    const pendingTabRef = useRef<ActiveTab | null>(null);

    // Отслеживаем состояние редактирования
    const handleEditingStateChange = useCallback(
      (editing: boolean, changes: boolean) => {
        setIsEditing(editing);
        setHasChanges(changes);
        // Уведомляем родительский компонент об изменении состояния редактирования
        onEditingStateChange?.(editing);
      },
      [onEditingStateChange]
    );
    const handleValueChange = useCallback(
      (value: string) => {
        const newTab = value as ActiveTab;

        // Проверяем, есть ли несохраненные изменения
        if (isEditing && hasChanges && newTab !== activeTab) {
          pendingTabRef.current = newTab;
          setShowConfirmDialog(true);
          return;
        }

        onTabChange(newTab);
      },
      [onTabChange, isEditing, hasChanges, activeTab]
    );

    const handleConfirmSaveAndSwitch = useCallback(async () => {
      try {
        if (onSaveRequest) {
          await onSaveRequest();
        }
        if (pendingTabRef.current) {
          onTabChange(pendingTabRef.current);
          pendingTabRef.current = null;
        }
      } catch (error) {
        console.error("Ошибка при сохранении:", error);
      } finally {
        setShowConfirmDialog(false);
      }
    }, [onSaveRequest, onTabChange]);

    const handleConfirmDiscard = useCallback(() => {
      if (pendingTabRef.current) {
        onTabChange(pendingTabRef.current);
        pendingTabRef.current = null;
      }
      setShowConfirmDialog(false);
    }, [onTabChange]);

    const handleCancelSwitch = useCallback(() => {
      pendingTabRef.current = null;
      setShowConfirmDialog(false);
    }, []);

    return (
      <Tabs
        value={activeTab}
        onValueChange={handleValueChange}
        className="w-full"
      >
        {/* Браузерные вкладки */}
        <div className="border-b border-border bg-gradient-to-r from-muted/30 to-muted/10 border-l-4 border-l-brand-500 border-r border-r-border/50 shadow-sm">
          <TabsList className="h-auto p-0 bg-transparent border-none ml-2">
            <TabsTrigger value="processed" className={TAB_TRIGGER_STYLES}>
              {t('records.tabs.document')}
            </TabsTrigger>
            <TabsTrigger
              value="transcript"
              className={`${TAB_TRIGGER_STYLES} ${isEditing
                  ? "opacity-50 pointer-events-none cursor-not-allowed"
                  : ""
                }`}
              disabled={isEditing}
            >
              {t('records.tabs.transcript')}
            </TabsTrigger>
          </TabsList>
        </div>

        {/* Контент прямо под вкладками */}
        <div className="bg-gradient-to-br from-card to-muted/5 rounded-b-lg border border-t-0 border-border/50 border-l-4 border-l-brand-500 shadow-sm">
          <TabsContent value="transcript" className="m-0 p-4 sm:p-6">
            <TranscriptTab
              transcriptText={record.transcriptText}
              fileName={record.name}
              hideActions={true}
            />
          </TabsContent>
          <TabsContent value="processed" className="m-0 p-2 sm:p-0">
            <ProcessedTab
              processedText={record.postProcessingText}
              fileName={record.name}
              hideActions={readOnly}
              recordId={record.id}
              onUpdate={onProcessedTextUpdate}
              readOnly={readOnly}
              onEditingStateChange={handleEditingStateChange}
            />
          </TabsContent>
        </div>

        {/* Диалог подтверждения */}
        <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>{t('records.tabs.unsaved_title')}</DialogTitle>
              <DialogDescription>
                {t('records.tabs.unsaved_desc')}
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="flex-col sm:flex-row gap-2">
              <Button
                variant="outline"
                onClick={handleCancelSwitch}
                className="w-full sm:w-auto"
              >
                {t('common.cancel')}
              </Button>
              <Button
                variant="destructive"
                onClick={handleConfirmDiscard}
                className="w-full sm:w-auto"
              >
                {t('records.tabs.cancel_changes')}
              </Button>
              <Button
                onClick={handleConfirmSaveAndSwitch}
                className="w-full sm:w-auto"
              >
                {t('records.tabs.save_and_switch')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </Tabs>
    );
  }
);
